import React, {
  CSSProperties,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CheckBoxWrapper, PlainTypeWrapper } from './DataModelerCdwTableGridUI';
import TickCheckbox from '../../../../components/Checkbox';
import KubitTooltip from '../../../../components/KubitTooltip';
import {
  FieldType,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import {
  filterAndSortColumnsData,
  SortableFieldItemKey,
} from './useCdwTableColumnsData';
import {
  ICdwColumnsData,
  ICdwTableColumnData,
  IEditDerivedColumnModel,
} from './types';
import SelectTypeOverride from './SelectTypeOverride';
import { fieldTypeName } from '../utils';
import SelectTypeParamsOverride from './SelectTypeParamsOverride';
import DerivedDefinitionMetaDisplay from './DerivedDefinitionMetaDisplay';
import CdwTableGridRowMapToField from './CdwTableGridRowMapToField';
import { GridCellProps, MultiGrid } from 'react-virtualized';
import LongNameWithTooltip from '../LongNameWithTooltip';
import { EditableTextInput } from '../../../../components/Form';
import { DESCRIPTION_MAX_LENGTH } from '../../../../config/constants';
import { GridCell, HeaderGridCell } from '../DataModelerCommonUI';
import ModifiedGridCell from './ModifiedGridCell';
import { ModifiedIcon } from '../DataModelerShared';
import TableColumnName from './TableColumnName';
import { TableSortIcon } from '../../../../components/Icon';

const useGridHeaderRenderer = ({
  sortedAndFilteredColumnsData,
  selectColumns,
  unselectColumns,
  sortedBy,
  setSortedBy,
  sortedDesc,
  setSortedDesc,
}: {
  sortedAndFilteredColumnsData: ICdwTableColumnData[];
  selectColumns: (tableColumns: ITableColumnFieldsFragment[]) => void;
  unselectColumns: (tableColumns: ITableColumnFieldsFragment[]) => void;
  sortedBy: SortableFieldItemKey | null;
  setSortedBy: (sortedBy: SortableFieldItemKey | null) => void;
  sortedDesc: boolean;
  setSortedDesc: React.Dispatch<SetStateAction<boolean>>;
}) => {
  const allAreSelected = useMemo(() => {
    const foundUnchecked = sortedAndFilteredColumnsData.some(
      (columnData) =>
        !columnData.isSelected && !columnData.isUnmappableColumnName,
    );
    return !foundUnchecked;
  }, [sortedAndFilteredColumnsData]);

  const selectAllNone = useCallback(() => {
    if (allAreSelected) {
      unselectColumns(
        sortedAndFilteredColumnsData
          .filter(
            (tableColumnData) =>
              tableColumnData.isSelected &&
              !tableColumnData.isRequired &&
              !tableColumnData.isUnmappableColumnName &&
              !tableColumnData.tableColumn.typeParams.customDefinition,
          )
          .map(({ tableColumn }) => tableColumn),
      );
    } else {
      const toSelect = sortedAndFilteredColumnsData.filter(
        (tableColumnData) =>
          !tableColumnData.isSelected &&
          !tableColumnData.isUnmappableColumnName,
      );
      selectColumns(toSelect.map(({ tableColumn }) => tableColumn));
    }
  }, [
    allAreSelected,
    sortedAndFilteredColumnsData,
    unselectColumns,
    selectColumns,
  ]);

  const onColumnHeaderClick = useCallback(() => {
    if (sortedBy === 'column') {
      if (sortedDesc) {
        // unsorted
        setSortedBy(null);
      }
      setSortedDesc((prev) => !prev);
    } else {
      setSortedBy('column');
      setSortedDesc(false);
    }
  }, [setSortedBy, setSortedDesc, sortedBy, sortedDesc]);

  const onTypeHeaderClick = useCallback(() => {
    if (sortedBy === 'type') {
      setSortedDesc((prev) => !prev);
    } else {
      setSortedBy('type');
      setSortedDesc(false);
    }
  }, [setSortedBy, setSortedDesc, sortedBy]);

  const onFieldHeaderClick = useCallback(() => {
    if (sortedBy === 'fieldName') {
      setSortedDesc((prev) => !prev);
    } else {
      setSortedBy('fieldName');
      setSortedDesc(false);
    }
  }, [setSortedBy, setSortedDesc, sortedBy]);

  const onDescriptionHeaderClick = useCallback(() => {
    if (sortedBy === 'description') {
      setSortedDesc((prev) => !prev);
    } else {
      setSortedBy('description');
      setSortedDesc(false);
    }
  }, [setSortedBy, setSortedDesc, sortedBy]);

  const onIsModifiedHeaderClick = useCallback(() => {
    if (sortedBy === 'isModified') {
      setSortedDesc((prev) => !prev);
    } else {
      setSortedBy('isModified');
      setSortedDesc(false);
    }
  }, [setSortedBy, setSortedDesc, sortedBy]);

  return useCallback(
    ({
      columnIndex,
      key,
      style,
    }: {
      columnIndex: number;
      rowIndex: number;
      key: string;
      style: CSSProperties;
    }) => {
      switch (columnIndex) {
        case 0:
          return (
            <HeaderGridCell
              key={`${key}-hdr-${columnIndex}`}
              style={style}
              $clickable={true}
              onClick={selectAllNone}
              $isHeader={true}
            >
              <CheckBoxWrapper>
                <TickCheckbox checked={allAreSelected} />
              </CheckBoxWrapper>
            </HeaderGridCell>
          );
        case 1:
          return (
            <HeaderGridCell
              key={`${key}-hdr-${columnIndex}`}
              style={style}
              $clickable={true}
              onClick={onColumnHeaderClick}
              $isHeader={true}
            >
              Column
              {sortedBy === 'column' && (
                <TableSortIcon isSortAsc={!sortedDesc} />
              )}
            </HeaderGridCell>
          );
        case 2:
          return (
            <HeaderGridCell
              key={`${key}-hdr-${columnIndex}`}
              style={style}
              $clickable={true}
              onClick={onTypeHeaderClick}
              $isHeader={true}
            >
              <PlainTypeWrapper>Type</PlainTypeWrapper>
              {sortedBy === 'type' && <TableSortIcon isSortAsc={!sortedDesc} />}
            </HeaderGridCell>
          );
        case 3:
          return (
            <HeaderGridCell
              key={`${key}-hdr-${columnIndex}`}
              style={style}
              $isHeader={true}
            >
              Type Info
            </HeaderGridCell>
          );
        case 4:
          return (
            <HeaderGridCell
              key={`${key}-hdr-${columnIndex}`}
              style={style}
              $clickable={true}
              onClick={onFieldHeaderClick}
              $isHeader={true}
            >
              Field
              {sortedBy === 'fieldName' && (
                <TableSortIcon isSortAsc={!sortedDesc} />
              )}
            </HeaderGridCell>
          );
        case 5:
          return (
            <HeaderGridCell
              key={`${key}-hdr-${columnIndex}`}
              style={style}
              $clickable={true}
              onClick={onDescriptionHeaderClick}
              $isHeader={true}
            >
              Description
              {sortedBy === 'description' && (
                <TableSortIcon isSortAsc={!sortedDesc} />
              )}
            </HeaderGridCell>
          );
        case 6:
          return (
            <HeaderGridCell
              key={`${key}-hdr-${columnIndex}`}
              style={style}
              $clickable={true}
              onClick={onIsModifiedHeaderClick}
              $isHeader={true}
            >
              <KubitTooltip content="Modified" noOpenDelay={true}>
                <ModifiedIcon isClickable={true} />
              </KubitTooltip>
              {sortedBy === 'isModified' && (
                <TableSortIcon isSortAsc={!sortedDesc} />
              )}
            </HeaderGridCell>
          );
        default:
          throw new Error(
            `Invalid column index in CDW Table Grid: ${columnIndex}`,
          );
      }
    },
    [
      allAreSelected,
      onColumnHeaderClick,
      onDescriptionHeaderClick,
      onFieldHeaderClick,
      onIsModifiedHeaderClick,
      onTypeHeaderClick,
      selectAllNone,
      sortedBy,
      sortedDesc,
    ],
  );
};

const useGridRowRenderer = ({
  viewKey,
  tableKey,
  isEditMode,
  onAddSelectedColumn,
  onRemoveSelectedColumn,
  canEditDerivedColumn,
  setEditedDerivedColumn,
  rowCount,
}: {
  viewKey: string;
  tableKey: string;
  isEditMode: boolean;
  onAddSelectedColumn: (tableColumn: ITableColumnFieldsFragment) => void;
  onRemoveSelectedColumn: (tableColumn: ITableColumnFieldsFragment) => void;
  canEditDerivedColumn: (columnData: ICdwTableColumnData) => boolean;
  setEditedDerivedColumn: (columnData: ICdwTableColumnData) => void;
  rowCount: number;
}) => {
  return useCallback(
    (
      columnData: ICdwTableColumnData,
      {
        columnIndex,
        rowIndex,
        key,
        style,
      }: {
        columnIndex: number;
        rowIndex: number;
        key: string;
        style: CSSProperties;
      },
    ) => {
      const {
        tableColumn,
        isRequired,
        isSelected,
        description,
        setDescription,
        // viewColumn,
        // setViewColumn,
        isModified,
        isNew,
        fieldName,
        // setFieldName,
        // fieldClass,
        // setFieldClass,
        typeInput,
        setTypeInput,
        isUnmappableColumnName,
      } = columnData;

      const canMapUnmap = isEditMode && !isUnmappableColumnName;

      // XXX force the last 5 rows to expand menus upwards, if there are at least 5 rows
      // above them. 'auto' does not seem to work correctly for the last grid rows
      // and the menu contents get truncated as it expands downwards.
      const menusUp = rowIndex > 5 && rowIndex > rowCount - 5;

      const cellClickHandler = () => {
        if (!canMapUnmap) {
          return;
        }
        if (isSelected) {
          if (isRequired) {
            return;
          }
          onRemoveSelectedColumn(tableColumn);
        } else {
          onAddSelectedColumn(tableColumn);
        }
      };

      const checkBoxPopupContent = isEditMode
        ? isRequired
          ? 'This column is required and can not be unmapped'
          : isUnmappableColumnName
            ? 'This column has unsupported name and can not be mapped directly. ' +
              'Please use a derived column with appropriate escaping'
            : isSelected
              ? 'Mapped column. Click to unmap'
              : 'Not mapped column. Click to map to field'
        : isSelected
          ? 'Mapped column'
          : 'Not mapped column';

      switch (columnIndex) {
        case 0:
          return (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              onClick={cellClickHandler}
              $clickable={canMapUnmap}
              $isEven={rowIndex % 2 === 0}
            >
              <KubitTooltip content={checkBoxPopupContent} noOpenDelay={true}>
                <CheckBoxWrapper>
                  <TickCheckbox
                    checked={isSelected}
                    disabled={!canMapUnmap || isRequired}
                  />
                </CheckBoxWrapper>
              </KubitTooltip>
            </GridCell>
          );
        case 1:
          const canEditDefinition =
            isEditMode &&
            !!tableColumn.typeParams.customDefinition &&
            canEditDerivedColumn(columnData);
          return (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              onClick={!isSelected ? cellClickHandler : undefined}
              $clickable={canMapUnmap && !isSelected}
              $isEven={rowIndex % 2 === 0}
            >
              <TableColumnName
                isDerived={!!tableColumn.typeParams.customDefinition}
                columnName={tableColumn.column}
                canEditDefinition={canEditDefinition}
                onEditDefinitionClick={() => setEditedDerivedColumn(columnData)}
              />
            </GridCell>
          );
        case 2:
          return (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              $isEven={rowIndex % 2 === 0}
            >
              {canMapUnmap && typeInput ? (
                <SelectTypeOverride
                  typeInput={typeInput}
                  setTypeInput={setTypeInput}
                />
              ) : (
                <PlainTypeWrapper>
                  {fieldTypeName(tableColumn.type)}
                </PlainTypeWrapper>
              )}
            </GridCell>
          );
        case 3:
          return (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              $isEven={rowIndex % 2 === 0}
            >
              {canMapUnmap && typeInput ? (
                <SelectTypeParamsOverride
                  typeInput={typeInput}
                  setTypeInput={setTypeInput}
                />
              ) : (
                <>
                  {tableColumn.type === FieldType.Timestamp && (
                    <>TZ: {tableColumn.typeParams.fixedTz ?? 'None'}</>
                  )}
                  {tableColumn.type === FieldType.Array && (
                    <>
                      Items type:{' '}
                      {fieldTypeName(
                        tableColumn.typeParams.arrayItemType ??
                          FieldType.String,
                      )}
                    </>
                  )}
                </>
              )}
              {!!tableColumn.typeParams.customDefinition && (
                <DerivedDefinitionMetaDisplay
                  definition={tableColumn.typeParams.customDefinition}
                />
              )}
            </GridCell>
          );
        case 4:
          return isSelected ? (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              $isEven={rowIndex % 2 === 0}
            >
              <CdwTableGridRowMapToField
                isEditMode={canMapUnmap}
                tableKey={tableKey}
                viewKey={viewKey}
                columnData={columnData}
                menuUp={menusUp}
              />
            </GridCell>
          ) : (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              onClick={cellClickHandler}
              $clickable={canMapUnmap}
              $isEven={rowIndex % 2 === 0}
            />
          );
        case 5:
          return isSelected ? (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              $isEven={rowIndex % 2 === 0}
            >
              <EditableTextInput
                disabled={!canMapUnmap}
                inputSize={'XS'}
                value={description}
                onSubmit={setDescription}
                limit={DESCRIPTION_MAX_LENGTH}
              />
            </GridCell>
          ) : (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              onClick={cellClickHandler}
              $clickable={canMapUnmap}
              $isEven={rowIndex % 2 === 0}
            >
              <LongNameWithTooltip name={description} />
            </GridCell>
          );
        case 6:
          return isSelected ? (
            <ModifiedGridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              isModified={isModified}
              isNew={isNew}
              tableKey={tableKey}
              viewKey={viewKey}
              fieldName={fieldName}
              tableColumn={tableColumn}
              style={style}
              rowIndex={rowIndex}
            />
          ) : (
            <GridCell
              key={`${key}-${columnData.column}-${columnIndex}`}
              style={style}
              onClick={cellClickHandler}
              $clickable={canMapUnmap}
              $isEven={rowIndex % 2 === 0}
            />
          );
        default:
          throw new Error(
            `Invalid column index in CDW Table Grid: ${columnIndex}`,
          );
      }
    },
    [
      isEditMode,
      onAddSelectedColumn,
      onRemoveSelectedColumn,
      tableKey,
      viewKey,
      canEditDerivedColumn,
      setEditedDerivedColumn,
      rowCount,
    ],
  );
};

export const useCdwTableGridCellRenderer = ({
  searchQuery,
  isEditMode,
  viewKey,
  tableKey,
  columnsData,
  editDerivedColumnModel,
}: {
  searchQuery: string;
  isEditMode: boolean;
  viewKey: string;
  tableKey: string;
  columnsData: ICdwColumnsData;
  editDerivedColumnModel: IEditDerivedColumnModel;
}) => {
  const [sortedBy, setSortedBy] = useState<SortableFieldItemKey | null>(null);
  const [sortedDesc, setSortedDesc] = useState(false);
  const [confirmUnmapModalColumn, setConfirmUnmapModalColumn] =
    useState<ITableColumnFieldsFragment | null>(null);

  const { columns, selectColumns, unselectColumns } = columnsData;

  const sortedAndFilteredColumnsData: ICdwTableColumnData[] = useMemo(() => {
    return filterAndSortColumnsData(columns, searchQuery, sortedBy, sortedDesc);
  }, [columns, searchQuery, sortedBy, sortedDesc]);

  const onAddSelectedColumn = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      selectColumns([tableColumn]);
    },
    [selectColumns],
  );

  const unselectColumn = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      if (
        editDerivedColumnModel.editedDerivedColumn?.columnName ===
        tableColumn.column
      ) {
        editDerivedColumnModel.cancelEditedDerivedColumn();
      }
      unselectColumns([tableColumn]);
    },
    [editDerivedColumnModel, unselectColumns],
  );

  const onRemoveSelectedColumn = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      if (tableColumn.typeParams.customDefinition) {
        setConfirmUnmapModalColumn(tableColumn);
      } else {
        unselectColumn(tableColumn);
      }
    },
    [unselectColumn],
  );

  const onConfirmUnmapColumn = useCallback(() => {
    if (confirmUnmapModalColumn) {
      unselectColumn(confirmUnmapModalColumn);
    }
    setConfirmUnmapModalColumn(null);
  }, [confirmUnmapModalColumn, unselectColumn]);

  const onCancelUnmapColumn = useCallback(() => {
    setConfirmUnmapModalColumn(null);
  }, []);

  const headerRenderer = useGridHeaderRenderer({
    sortedAndFilteredColumnsData,
    selectColumns,
    unselectColumns,
    sortedBy,
    sortedDesc,
    setSortedBy,
    setSortedDesc,
  });

  const rowCount = sortedAndFilteredColumnsData.length;

  const rowRenderer = useGridRowRenderer({
    viewKey,
    tableKey,
    isEditMode,
    onAddSelectedColumn,
    onRemoveSelectedColumn,
    canEditDerivedColumn: editDerivedColumnModel.canEditDerivedColumn,
    setEditedDerivedColumn: editDerivedColumnModel.setEditedDerivedColumn,
    rowCount,
  });

  const cellRenderer = useCallback(
    ({ columnIndex, key, rowIndex, style }: GridCellProps) => {
      if (rowIndex === 0) {
        return headerRenderer({ columnIndex, key, rowIndex, style });
      }
      const listIndex = rowIndex - 1;
      const columnData = sortedAndFilteredColumnsData[listIndex];
      if (!columnData) {
        return null;
      }
      return rowRenderer(columnData, { columnIndex, key, rowIndex, style });
    },
    [sortedAndFilteredColumnsData, rowRenderer, headerRenderer],
  );

  return {
    cellRenderer,
    onConfirmUnmapColumn,
    rowCount,
    onCancelUnmapColumn,
    confirmUnmapModalColumn,
  };
};

// '32px minmax(200px,1fr)  100px 200px minmax(230px,1fr) minmax(230px,1fr) 32px';
const MIN_COLUMN_WIDTHS = [32, 200, 100, 200, 230, 230, 32];
const MIN_WIDTH = MIN_COLUMN_WIDTHS.reduce((prev, cur) => prev + cur, 0); // 1024
const SCROLL_WIDTH = 8;

const calcColumWidths = (gridWidth: number, hasScroll: boolean) => {
  const toDistribute = gridWidth - MIN_WIDTH - (hasScroll ? SCROLL_WIDTH : 0);
  if (toDistribute <= 0) {
    return MIN_COLUMN_WIDTHS;
  }
  const toDistributePerCell = Math.floor(toDistribute / 3); // 3 cells subject to size change
  const toDistributeReminder = toDistribute % 3;
  return [
    MIN_COLUMN_WIDTHS[0],
    MIN_COLUMN_WIDTHS[1] + toDistributePerCell,
    MIN_COLUMN_WIDTHS[2],
    MIN_COLUMN_WIDTHS[3],
    MIN_COLUMN_WIDTHS[4] + toDistributePerCell,
    MIN_COLUMN_WIDTHS[5] + toDistributePerCell + toDistributeReminder,
    MIN_COLUMN_WIDTHS[6],
  ];
};

export const useCdwTableGridColumnWidths = ({
  current,
  hasScrollbar,
}: {
  current: MultiGrid | null;
  hasScrollbar: boolean;
}) => {
  const initialWidth = current?.props.width ?? MIN_WIDTH;
  const [columnWidths, setColumnWidths] = useState(
    calcColumWidths(initialWidth, hasScrollbar),
  );

  const getColumnWidth = useCallback(
    ({ index }): number => {
      return columnWidths[index] ?? 100;
    },
    [columnWidths],
  );

  const onResize = useCallback(() => {
    if (current) {
      setColumnWidths(calcColumWidths(current.props.width, hasScrollbar));
    }
  }, [current, hasScrollbar]);

  // Trigger re-compute whenever our widths change
  useEffect(() => {
    current?.recomputeGridSize();
  }, [columnWidths, current]);

  return {
    getColumnWidth,
    onResize,
  };
};
